/**
 * DataState-Wrapper aus Datenmodell § 5.
 *
 * Jede Datenoperation in V5 muss in diesen Wrapper gepackt werden — als globaler
 * Hook (useDataState) und als Backend-Pattern.
 *
 * Regeln:
 * - Timeout für Fetch: 8-12 s, danach status='error' mit retry_possible=true
 * - Daten älter als ttl_seconds → status='stale', Daten werden trotzdem angezeigt
 *   mit Hinweis "Letzter erfolgreicher Abruf: ..."
 * - Keine Daten = explizit (status='no_data'), nicht "-" oder "Lade..."
 * - Skeleton-Komponenten greifen während status='loading'
 */

import type { LoadingStatus } from './enums';

export interface DataStateError {
  code: string;
  message: string;
  retry_possible: boolean;
  /** Optional: HTTP-Status, falls bekannt */
  status?: number;
}

export type DataStateSource = 'fresh' | 'cache' | 'fallback';

export interface DataState<T> {
  status: LoadingStatus;
  data: T | null;
  error: DataStateError | null;
  /** ISO timestamp des letzten erfolgreichen Abrufs */
  last_successful_fetch_at: string | null;
  next_retry_at: string | null;
  source: DataStateSource;
  ttl_seconds: number;
}

/**
 * Hilfs-Konstruktoren für die häufigsten States.
 * Verwendung in Tests, in Backend-Responses, oder beim Initialisieren.
 */
export const DataStateHelpers = {
  idle<T>(ttl_seconds = 300): DataState<T> {
    return {
      status: 'idle',
      data: null,
      error: null,
      last_successful_fetch_at: null,
      next_retry_at: null,
      source: 'fresh',
      ttl_seconds,
    };
  },

  loading<T>(previous?: DataState<T>): DataState<T> {
    return {
      status: 'loading',
      data: previous?.data ?? null,
      error: null,
      last_successful_fetch_at: previous?.last_successful_fetch_at ?? null,
      next_retry_at: null,
      source: previous?.source ?? 'fresh',
      ttl_seconds: previous?.ttl_seconds ?? 300,
    };
  },

  success<T>(data: T, ttl_seconds = 300): DataState<T> {
    return {
      status: 'success',
      data,
      error: null,
      last_successful_fetch_at: new Date().toISOString(),
      next_retry_at: null,
      source: 'fresh',
      ttl_seconds,
    };
  },

  noData<T>(ttl_seconds = 300): DataState<T> {
    return {
      status: 'no_data',
      data: null,
      error: null,
      last_successful_fetch_at: new Date().toISOString(),
      next_retry_at: null,
      source: 'fresh',
      ttl_seconds,
    };
  },

  error<T>(error: DataStateError, previous?: DataState<T>): DataState<T> {
    return {
      status: 'error',
      data: previous?.data ?? null,
      error,
      last_successful_fetch_at: previous?.last_successful_fetch_at ?? null,
      next_retry_at: error.retry_possible
        ? new Date(Date.now() + 5000).toISOString()
        : null,
      source: previous?.source ?? 'fresh',
      ttl_seconds: previous?.ttl_seconds ?? 300,
    };
  },
} as const;
